import {
	BaseHttpService,
	CmsApi,
	ConfiguratorApi,
	createNetworkAwareService,
	GraderApi,
	PlatformApi,
	UrlShortenerApi,
} from "@pandas/http-client";
import { servicesOrchestratorValues } from "@/config/services";
import { getLogger } from "@/lib/logging/logger";
import { networkManager } from "@/utils/networkManager";

const logger = getLogger();

export type VisionAiApiClient = {
	platform: PlatformApi;
	grader: GraderApi;
	configurator: ConfiguratorApi;
	cms: CmsApi;
	urlShortener: UrlShortenerApi;
	registerGetAccessToken: (fn: (() => Promise<string | undefined>) | null) => void;
};

export function createVisionAiApiClient(): VisionAiApiClient {
	const { urls } = servicesOrchestratorValues;

	let _getAccessToken: (() => Promise<string | undefined>) | null = null;

	async function getAuthHeaders(): Promise<Record<string, string>> {
		// No getter: the init window before Auth0 is ready, or a session with nobody signed in.
		if (!_getAccessToken) return {};
		try {
			const token = await _getAccessToken();
			if (token) return { Authorization: `Bearer ${token}` };
			// Getter is wired but yielded no token: the request goes out unauthenticated, which
			// is the direct cause of null-user sessions. Log it to trace any residual post-hotfix.
			logger.warning("NETWORK", "Authed request sent without bearer: token getter returned empty");
			return {};
		} catch (error) {
			logger.warning("NETWORK", "Authed request sent without bearer: token acquisition failed", {
				reason: error instanceof Error ? error.message : String(error),
			});
			return {};
		}
	}

	function makeHttp(baseUrl: string, withAuth = false) {
		return createNetworkAwareService(
			new BaseHttpService(baseUrl, logger, withAuth ? getAuthHeaders : undefined),
			networkManager.queueRequest.bind(networkManager),
		);
	}

	const platform = new PlatformApi(
		makeHttp(`${urls.platform}/api/v1`, true),
		makeHttp(`${urls.platform}/api/v2`),
		makeHttp(""),
	);
	const grader = new GraderApi(makeHttp(urls.grader));
	const configurator = new ConfiguratorApi(makeHttp(`${urls.platform}/api/v2`), "vision");
	const cms = new CmsApi(makeHttp(urls.cms));
	const urlShortener = new UrlShortenerApi(makeHttp(urls.urlShortener));

	return {
		platform,
		grader,
		configurator,
		cms,
		urlShortener,
		registerGetAccessToken(fn) {
			_getAccessToken = fn;
		},
	};
}
